import {createComponent} from '../bridge/create-component';
import {EVENT} from '../bridge/constants';
import type {BridgeComponentOptions} from '../bridge/types';
import {createToken, getPaymentMethods} from '../api';
import {config} from '../config';
import {containerTemplate} from '../container';
import {loadScript, parsePx, validateComponentProps} from '../utils';
import type {ShippingOption} from '../types';
import {
  normalizePayPalCallbackAddress,
  normalizePayPalPayer,
  normalizePayPalShippingDetails
} from './normalize-address';
import type {PayPalProps} from './types';

export * from './types';

const namespace = 'monei-paypal';

const validatePayPalProps = ({props}: {props: PayPalProps}) => {
  validateComponentProps({props});
  if (props.requestShipping && props.paymentId) {
    throw new Error('requestShipping requires accountId flow. Use accountId instead of paymentId.');
  }
};

// Convert minor units (cents) to decimal string for PayPal patch operations
const toDecimal = (amount: number): string => (amount / 100).toFixed(2);

// Build PayPal order patch operations for amount/shipping option updates
const buildPatchOps = (
  amount: number | undefined,
  currency: string,
  shippingOptions?: ShippingOption[],
  selectedOptionId?: string
): any[] => {
  const ops: any[] = [];
  if (amount !== undefined) {
    ops.push({
      op: 'replace',
      path: "/purchase_units/@reference_id=='default'/amount",
      value: {currency_code: currency, value: toDecimal(amount)}
    });
  }
  if (shippingOptions?.length) {
    ops.push({
      op: 'replace',
      path: "/purchase_units/@reference_id=='default'/shipping/options",
      value: shippingOptions.map((opt, idx) => ({
        id: opt.id,
        label: opt.label,
        type: opt.type ?? 'SHIPPING',
        amount: {currency_code: currency, value: toDecimal(opt.amount)},
        selected: selectedOptionId ? opt.id === selectedOptionId : (opt.selected ?? idx === 0)
      }))
    });
  }
  return ops;
};

const onLoadPaypal = async (props: PayPalProps, state: any, src: string) => {
  const script = await loadScript(src, props.cspNonce, {namespace, cspNonce: props.cspNonce}, true);
  state.__scriptId = script.dataset.uidAuto;
  const defaultStyle: Record<string, any> = {
    layout: 'horizontal',
    tagline: false,
    height: 45
  };
  const radius = parsePx(props.style?.borderRadius ?? props.borderRadius);
  if (radius) defaultStyle.borderRadius = radius;
  const style = {...defaultStyle, ...props.style};
  style.height = parsePx(style.height, 45);
  props.onLoad?.(true);
  let token: string | undefined;
  let currentAmount = props.amount;
  let lastSelectedOption: ShippingOption | undefined;

  const buttonsConfig: Record<string, any> = {
    createOrder: async () => {
      try {
        const result = await createToken({
          paymentId: props.paymentId,
          accountId: props.accountId,
          sessionId: props.sessionId,
          amount: props.amount,
          currency: props.currency,
          transactionType: props.transactionType,
          paymentMethod: {
            paypal: props.requestShipping ? {shippingOptions: props.shippingOptions} : {}
          }
        });
        ({token} = result);
        return result.paypal?.orderId;
      } catch (error) {
        props.onError?.(error);
        return;
      }
    },
    onApprove: async (data: any, actions: any) => {
      if (!data.orderID) {
        props.onError?.(data);
        return;
      }
      if (props.requestShipping && actions?.order?.get) {
        let submitPayload: any = {
          token,
          paymentMethod: 'paypal' as const,
          finalAmount: currentAmount
        };
        try {
          const orderDetails = await actions.order.get();
          const payer = orderDetails?.payer;
          const purchaseUnit = orderDetails?.purchase_units?.[0];
          const shipping = purchaseUnit?.shipping;
          submitPayload = {
            token,
            paymentMethod: 'paypal' as const,
            finalAmount: currentAmount,
            billingDetails: normalizePayPalPayer(payer),
            shippingDetails: normalizePayPalShippingDetails(shipping),
            shippingOption: lastSelectedOption
          };
        } catch (error) {
          props.onError?.(error);
        }
        props.onSubmit(submitPayload);
      } else {
        props.onSubmit({token, paymentMethod: 'paypal'});
      }
    },
    onClick: function (_data: any, actions: any) {
      let shouldOpen = true;
      if (props.onBeforeOpen) {
        shouldOpen = props.onBeforeOpen();
      }
      if (!shouldOpen) return actions.reject();
    },
    style
  };

  // Add shipping callbacks when express checkout is enabled
  if (props.requestShipping) {
    buttonsConfig.onShippingAddressChange = async (data: any, actions: any) => {
      if (!props.onShippingAddressChange) return;
      const previousOption = lastSelectedOption;
      const previousAmount = currentAmount;
      try {
        const address = normalizePayPalCallbackAddress(data);
        const result = await props.onShippingAddressChange(address);
        if (result?.shippingOptions?.length) {
          lastSelectedOption =
            result.shippingOptions.find((o) => o.selected) ?? result.shippingOptions[0];
        }
        if (result?.amount !== undefined) currentAmount = result.amount;
        const ops = buildPatchOps(
          result?.amount,
          props.currency ?? 'EUR',
          result?.shippingOptions,
          lastSelectedOption?.id
        );
        if (ops.length > 0) await actions.order.patch(ops);
      } catch {
        lastSelectedOption = previousOption;
        currentAmount = previousAmount;
        return actions.reject();
      }
    };

    buttonsConfig.onShippingOptionsChange = async (data: any, actions: any) => {
      if (!props.onShippingOptionChange) return;
      const previousOption = lastSelectedOption;
      const previousAmount = currentAmount;
      try {
        const selectedOption: ShippingOption = {
          id: data?.selectedShippingOption?.id ?? '',
          label: data?.selectedShippingOption?.label ?? '',
          type: data?.selectedShippingOption?.type ?? 'SHIPPING',
          amount: Math.round(parseFloat(data?.selectedShippingOption?.amount?.value ?? '0') * 100)
        };
        lastSelectedOption = selectedOption;
        const result = await props.onShippingOptionChange(selectedOption);
        const ops = buildPatchOps(result?.amount, props.currency ?? 'EUR');
        if (result?.amount !== undefined) currentAmount = result.amount;
        if (ops.length > 0) await actions.order.patch(ops);
      } catch {
        lastSelectedOption = previousOption;
        currentAmount = previousAmount;
        return actions.reject();
      }
    };
  }

  await (window as any)[namespace].Buttons(buttonsConfig).render(state.__container);
};

const componentOptions: BridgeComponentOptions<PayPalProps> = {
  tag: namespace,
  url: config.paypalUrl,
  dimensions: {
    width: '100%',
    height: '0'
  },
  autoResize: {
    width: false,
    height: false
  },
  aliases: {
    style: 'innerStyle'
  },
  computedProps: {
    __getConfig: {
      factory: ({props}) => getPaymentMethods.bind(null, props),
      type: 'function'
    },
    __onLoadPayPal: {
      factory: ({props, state}) => onLoadPaypal.bind(null, props as PayPalProps, state),
      type: 'function'
    }
  },
  validate: validatePayPalProps,
  containerTemplate: (params) => {
    params.state.__container = params.container;
    if (!params.props.accountId && !params.props.paymentId) {
      throw new Error('You need to provide paymentId or accountId');
    }
    const container = containerTemplate(params);
    params.event.on(EVENT.DESTROY, () => {
      (window as any)[namespace]?.Buttons?.instances?.forEach((instance: any) => {
        instance.close();
      });
      const scriptId = params.state.__scriptId;
      if (scriptId) {
        const script = document.querySelector(`script[data-uid-auto="${scriptId}"]`);
        if (script) {
          script.remove();
        }
      }
    });
    return container;
  }
};

export const PayPal = createComponent<PayPalProps>(componentOptions);
